import { defaultTheme } from '@automata/mui-theme';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, {
  SelectProps as MuiTextFieldProps,
} from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { forwardRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FormLabel } from '../FormLabel';
import { type InfoIconPopoverProps } from '../InfoIconPopover';
import { Typography } from '../Typography';

type ExtraConfig = {
  helperText?: string;
  placeholder?: string;
};

export type SelectProps = Omit<MuiTextFieldProps, 'size' | 'popover'> &
  Partial<InfoIconPopoverProps> &
  ExtraConfig;

export const Select = forwardRef<unknown, SelectProps>(function Forwarded(
  {
    children,
    helperText,
    error,
    id = `select-${uuidv4()}`,
    disabled,
    label,
    infoPopover,
    infoPopoverMaxWidth,
    infoPopoverAnchorOrigin,
    infoPopoverTransformOrigin,
    value = '',
    placeholder = 'Select an option',
    required,
    ...restProps
  },
  ref
) {
  const labelId = `label-${uuidv4()}`;

  return (
    <Stack>
      {(label || infoPopover) && (
        <FormLabel
          id={labelId}
          required={required}
          infoPopover={infoPopover}
          infoPopoverMaxWidth={infoPopoverMaxWidth}
          infoPopoverAnchorOrigin={infoPopoverAnchorOrigin}
          infoPopoverTransformOrigin={infoPopoverTransformOrigin}
        >
          {label}
        </FormLabel>
      )}
      <FormControl>
        <MuiSelect
          id={id}
          ref={ref}
          labelId={labelId}
          size="small"
          displayEmpty
          value={value}
          defaultValue={value}
          error={error}
          disabled={disabled}
          {...restProps}
        >
          <MenuItem disabled value="">
            <Typography color={defaultTheme.palette.grey[400]}>
              {placeholder}
            </Typography>
          </MenuItem>
          {children}
        </MuiSelect>
        {helperText && (
          <FormHelperText error={error} disabled={disabled}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
});
